<script setup lang="ts">
import { Head, useForm } from "@inertiajs/vue3"
import { LoaderCircle } from "lucide-vue-next"
import { onBeforeUnmount, onMounted, ref } from "vue"

import InputError from "@/components/InputError.vue"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import AuthBase from "@/layouts/AuthLayout.vue"
import {
  passkeyAuthentication,
  passkeyAutocomplete,
  passkeyRegistration,
} from "@/lib/passkeys"
import { signInPath } from "@/routes"

const pageProps = defineProps<SessionPageProps>()

const autoCompleteAbortController = new AbortController()
const form = useForm({
  email: pageProps.email || "",
})

const isProcessingPasskey = ref(false)
const errorMessage = ref<string | null>(null)

type SessionPageProps = {
  email?: string
  registration?: PublicKeyCredentialCreationOptionsJSON
  authentication?: PublicKeyCredentialRequestOptionsJSON
  autocomplete_authentication?: PublicKeyCredentialRequestOptionsJSON
}

function autocompleteAbortRequest() {
  if (!autoCompleteAbortController.signal.aborted)
    autoCompleteAbortController.abort()
}

async function handleSubmit() {
  autocompleteAbortRequest()

  form.post(signInPath(), { preserveScroll: true })
}

async function verifyPasskey() {
  if (
    isProcessingPasskey.value ||
    !(pageProps.registration || pageProps.authentication)
  )
    return

  isProcessingPasskey.value = true
  errorMessage.value = null

  try {
    if (pageProps.registration) {
      await passkeyRegistration(pageProps.registration)
    } else if (pageProps.authentication) {
      await passkeyAuthentication(pageProps.authentication)
    }
  } catch (error) {
    errorMessage.value = `Passkey
      ${pageProps.registration ? "registration" : "authentication"} failed:
      ${error instanceof Error ? error.message : "Unknown error"}
    `
  } finally {
    isProcessingPasskey.value = false
  }
}

async function waitPasskeyAutocomplete() {
  if (!pageProps.autocomplete_authentication) return
  try {
    await passkeyAutocomplete(
      pageProps.autocomplete_authentication,
      autoCompleteAbortController,
    )
  } catch (error: unknown) {
    if (!(error instanceof Error && error.name === "AbortError"))
      console.error(error)
  }
}

onMounted(() => {
  waitPasskeyAutocomplete()
  verifyPasskey()
})

onBeforeUnmount(() => {
  autocompleteAbortRequest()
})
</script>

<template>
  <AuthBase
    title="Log in to your account"
    description="Enter your email to receive a sign-in link"
  >
    <Head title="Log in" />

    <form @submit.prevent="handleSubmit" class="flex flex-col gap-6">
      <div class="grid gap-6">
        <div class="grid gap-2">
          <Label for="email">Email address</Label>
          <Input
            id="email"
            v-model="form.email"
            type="email"
            required
            autofocus
            :tabindex="1"
            autocomplete="username webauthn"
            placeholder="email@example.com"
          />
          <InputError :messages="form.errors.email" />
        </div>

        <Button
          type="submit"
          class="mt-4 w-full"
          :tabindex="2"
          :disabled="form.processing"
        >
          <LoaderCircle v-if="form.processing" class="h-4 w-4 animate-spin" />
          <span v-if="isProcessingPasskey">Setting up passkey...</span>
          <span v-else>Get confirmation link</span>
        </Button>
      </div>
    </form>
  </AuthBase>
</template>
